// 2-D Dynamic Programming — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as dp1d.js: concept → internals →
// applied templates → space optimization → errors → complexity → beginner drills → cheatsheet → playground.

import dp2dDrills from './dp2dDrills';
const drill = id => dp2dDrills.find(d => d.id === id);

const dp2d = {
  key: '2-D Dynamic Programming',
  tagline: 'When one moving index isn\'t enough to describe where you are, the dp table becomes a grid instead of a line.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to 2-D Dynamic Programming',
      type: 'text',
      body: [
        "2-D Dynamic Programming applies the moment a subproblem's identity needs TWO independent numbers to describe it, instead of one — a row and a column on a grid, or a position in one string alongside a position in a second string. The dp table becomes a grid, dp[i][j], instead of a line.",
        "Nothing about the underlying discipline changes: each cell is still computed exactly once, from a small, fixed set of neighboring cells, and never recomputed. The only thing that grows from 1-D DP is the SHAPE of the table — and, correspondingly, the shape of \"nearby\" a transition is allowed to read from.",
      ],
    },
    {
      id: 'why',
      title: 'Why 2-D Dynamic Programming?',
      type: 'text',
      body: [
        "Many problems naturally have two moving \"cursors\" at once: a robot's row AND column position on a grid, or a pointer into one string alongside a pointer into a second string. Trying to force that into a 1-D table loses information — dp[i] alone can't capture \"how far along both strings we are\" at the same time.",
        "2-D DP is simply 1-D DP's exact same discipline — define the state, find the base case, write the transition, fill in a valid order — applied to a grid of states instead of a line of them. The recurrence usually still only reads a handful of neighboring cells; it's just that \"neighboring\" now means up, left, and sometimes diagonal, instead of just \"the one or two entries before this one.\"",
      ],
    },
    {
      id: 'analogy',
      title: 'How 2-D DP Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A City Grid With a Delivery Robot, and Two Fingers Walking Two Strings',
      body: [
        "Picture a delivery robot navigating a city laid out as a perfect grid, only ever allowed to move right or down at each intersection. The number of distinct routes to reach any specific intersection depends on exactly two earlier intersections: the one directly above, and the one directly to the left. Nothing else matters — not how the robot got there, just how many ways exist to reach each of those two neighbors. That's Unique Paths and Minimum Path Sum in one picture.",
        "Now picture comparing two strips of text, one finger resting on a position in each. At every pair of positions, there are only ever a few real choices: if the characters under both fingers match, advance both fingers together; if they don't, advance just one finger (or try both ways, in turn) and see which leads somewhere better. The grid dp[i][j] records the best answer for every possible PAIR of finger positions at once — that's the shape behind Longest Common Subsequence and Edit Distance.",
      ],
    },
    {
      id: 'visualize',
      title: 'Visualizing the DP Grid',
      type: 'grid-diagram',
      body: [
        "This is the Unique Paths grid for a 3×3 board, fully filled in. Every cell sums the value directly above it and the value directly to its left — exactly two neighbors, never more, which is why a grid this simple can be filled with a single pass.",
        "The bottom-right cell, 6, is the final answer: six distinct routes from the top-left corner to the bottom-right, moving only right or down.",
      ],
      demo: {
        grid: [[1, 1, 1], [1, 2, 3], [1, 3, 6]],
        highlightCells: [[2, 2]],
        note: 'dp[2][2] = dp[1][2] + dp[2][1] = 3 + 3 = 6.',
      },
    },
    {
      id: 'template',
      title: 'The Universal 2-D DP Recipe',
      type: 'code',
      code:
`int[][] solve(/* two independent state dimensions, e.g. row i and column j */) {
    int[][] dp = new int[rows][cols];

    // 1. Base case(s): usually the first row and/or first column,
    //    computed directly with no dependency on anything else.
    for (/* first row */) dp[0][j] = /* known directly */;
    for (/* first column */) dp[i][0] = /* known directly */;

    // 2. Transition: dp[i][j] built from a FIXED, SMALL set of neighboring cells —
    //    almost always dp[i-1][j], dp[i][j-1], and sometimes dp[i-1][j-1].
    for (int i = 1; i < rows; i++) {
        for (int j = 1; j < cols; j++) {
            dp[i][j] = /* combine the neighboring cells per the problem's rule */;
        }
    }
    return dp;
}`,
      note: 'Nearly every 2-D DP problem in this pattern fills the SAME three neighbors — up, left, and diagonally up-left. The only thing that changes problem to problem is which of those three are actually used, and how they\'re combined: a sum for counting, a min/max for optimization, an equality check for string alignment.',
    },
    {
      id: 'pathcounttemplate',
      title: 'Applied: The Grid-Path Recurrence',
      type: 'code',
      code:
`int uniquePaths(int m, int n) {
    int[][] dp = new int[m][n];
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            dp[i][j] = (i == 0 || j == 0) ? 1 : dp[i - 1][j] + dp[i][j - 1];
        }
    }
    return dp[m - 1][n - 1];
}`,
      note: 'dp[i][j] here means "the number of distinct routes to reach cell (i,j)." Only two neighbors are ever combined — up and left — with a SUM, since every route through either neighbor is a distinct, valid route to (i,j) itself. Minimum Path Sum and Triangle use this exact same shape, swapping the sum for a min() plus the current cell\'s own cost.',
    },
    {
      id: 'twostringtemplate',
      title: 'Applied: The Two-String Alignment Recurrence',
      type: 'code',
      code:
`int longestCommonSubsequence(String text1, String text2) {
    int m = text1.length(), n = text2.length();
    int[][] dp = new int[m + 1][n + 1];
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            dp[i][j] = (text1.charAt(i - 1) == text2.charAt(j - 1))
                ? dp[i - 1][j - 1] + 1
                : Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
    }
    return dp[m][n];
}`,
      note: 'dp[i][j] here means "the LCS length using only the first i characters of text1 and the first j characters of text2." A match reads the DIAGONAL neighbor (both fingers were one step behind); a mismatch takes the better of moving just one finger forward. Edit Distance reuses the exact same three neighbors, just with a different combining rule: 1 + min of all three, instead of a match-or-max choice.',
    },
    {
      id: 'spaceoptimization',
      title: 'Space Optimization: Row Compression',
      type: 'code',
      code:
`int uniquePathsRowCompressed(int m, int n) {
    int[] prevRow = new int[n];
    Arrays.fill(prevRow, 1);          // base case: the entire first row is 1
    for (int i = 1; i < m; i++) {
        int[] curRow = new int[n];
        curRow[0] = 1;                 // base case: the first column is 1
        for (int j = 1; j < n; j++) {
            curRow[j] = prevRow[j] + curRow[j - 1];
        }
        prevRow = curRow;
    }
    return prevRow[n - 1];
}`,
      note: 'Since row i only ever reads from row i-1 (and the current row being built), the FULL grid never needs to stay in memory — only the previous row. This collapses O(m·n) space down to O(n), a very common interview follow-up once the full-grid solution is already working correctly.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Getting the base case (first row/column) wrong for the specific problem',
          why: 'Unique Paths\' first row and column are all 1s (only one way to travel in a straight line), but Minimum Path Sum\'s first row and column are CUMULATIVE SUMS (each cell adds the cost of every cell before it). Copying one problem\'s base case onto another silently produces the wrong table starting from row 0.',
          fix: 'Derive the base case from the problem\'s own rule, not from memory of a similar-looking problem — ask "what is the ONLY possible path or alignment to this edge cell?" and compute it directly from that.',
        },
        {
          mistake: 'Mixing up 0-indexed strings with 1-indexed dp dimensions',
          why: 'LCS and Edit Distance conventionally size the dp grid as (m+1) × (n+1), where dp[i][j] represents the first i characters of one string and the first j of the other — but the strings themselves stay 0-indexed, so accessing text1.charAt(i-1) instead of text1.charAt(i) is easy to get backwards.',
          fix: 'Be explicit that dp[i][j] means "using PREFIXES of length i and j" — the corresponding characters being compared are always at index i-1 and j-1 in the original 0-indexed strings.',
        },
        {
          mistake: 'Forgetting that some problems also need the diagonal neighbor',
          why: 'Grid path problems (Unique Paths, Minimum Path Sum) only ever need up and left — but string-alignment problems (LCS, Edit Distance) also read dp[i-1][j-1], the diagonal. Omitting it either breaks the "characters match" case entirely, or compiles fine while silently returning a wrong, usually too-small, answer.',
          fix: 'Before writing the transition, write out in words which neighboring cells this SPECIFIC problem actually depends on — don\'t assume every 2-D DP problem reads the same three.',
        },
        {
          mistake: 'Filling the grid in the wrong direction for a problem that reads "downward"',
          why: 'Most 2-D DP problems fill top-left to bottom-right, but Triangle Minimum Path Sum naturally reads more cleanly bottom-up, from the last row upward. Filling it top-down instead means the recurrence has to be re-derived — a naive top-down fill for that specific problem simply reads uninitialized cells.',
          fix: 'Match the fill order to the direction the transition actually reads: if dp[i][j] depends on the ROW BELOW it, fill starting from the bottom row and move upward, not from row 0 downward.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Grid path-counting (Unique Paths)', time: 'O(m · n)', note: 'Every cell sums exactly two neighbors — a single pass over the grid.' },
        { op: 'Grid min/max-cost (Minimum Path Sum, Triangle)', time: 'O(m · n)', note: 'Same single-pass shape, using min() or max() instead of addition.' },
        { op: 'Two-string alignment (LCS, Edit Distance)', time: 'O(m · n)', note: 'One cell per pair of prefix lengths — the grid is sized (m+1) × (n+1).' },
        { op: 'Boolean grid DP (Interleaving String)', time: 'O(m · n)', note: 'Same shape, but each cell holds a true/false feasibility instead of a number.' },
        { op: 'Maximal Square (reads 3 neighbors)', time: 'O(m · n)', note: 'Each cell takes the min of three neighbors — still one pass, since each cell is touched a constant number of times.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for 2-D DP when you see…',
      body: [
        'Two independent indices that genuinely both matter to describe "where you are" — a row and a column, or a position in string A alongside a position in string B.',
        'A 1-D DP recurrence attempt that needs "two numbers of context" instead of one to be well-defined.',
        'A request to compare or align two sequences (strings, arrays) character-by-character or element-by-element.',
        'Optimal substructure still holds, just across a grid of states rather than a line — the best answer at (i,j) is built entirely from a few neighboring cells.',
        'If a THIRD independent dimension seems necessary, double-check first whether it can be folded into a boolean or small extra state — true 3-D DP is comparatively rare in interviews.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('minimum-path-sum'),
            drill('unique-paths-drill'),
            drill('unique-paths-with-obstacles'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('longest-common-subsequence-drill'),
            drill('longest-common-substring'),
            drill('triangle-minimum-path-sum'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('edit-distance'),
            drill('maximal-square'),
            drill('interleaving-string'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with defining dp[i][j], its base case, and which neighboring cells its transition reads before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'The base case is usually the first row and/or column — but its VALUES depend entirely on the specific problem\'s rule; never assume one problem\'s base case matches another\'s.',
        'Nearly every transition reads at most 3 neighbors: up, left, and (for string-alignment problems) diagonally up-left.',
        'Counting problems (Unique Paths) sum their neighbors; cost problems (Minimum Path Sum) take a min/max of their neighbors plus the current cell\'s own cost; string problems compare characters to decide which neighbor(s) to read.',
        'dp[i][j] for two-string problems conventionally means "using the first i and first j characters" — the grid is sized (m+1)×(n+1), with actual string indices always one less.',
        'Space can almost always be compressed from O(m·n) to O(n) (or O(min(m,n))) by keeping only the previous row, once the full two-dimensional version is verified correct.',
        'If a problem needs to be solved bottom-up from the LAST row (like Triangle), match the fill direction to what the recurrence actually reads.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default dp2d;
